import { useCallback, useState } from 'react';
import type { ChangeEvent } from 'react';

import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';

import { PermissionEnum } from '@e-socna/data-layer';
import type { Id } from '@e-socna/data-layer';

import { sortedByThirdPartyName } from '../../components/ThirdParties/utils/by-third-party-name.util';
import { usePermissions } from '../../hooks/usePermissions.hook';
import { describeErrors } from '../../utils/back-errors.util';
import { AlertMessages } from '../../components/AlertMessages';

import { HandoverReportTable } from './HandoverReportTable';
import { useHandOverResponsibilitiesMutation } from './hooks/useHandOverResponsibilitiesMutation.hook';
import { useHandoverReplacementsQuery } from './hooks/useHandoverReplacementsQuery.hook';

interface Props {
  readonly personId: Id;
  readonly organizationId: Id;
}

// A member leaving an internal organization hands over to a replacement: the replacement receives
// the membership roles and is added where the member represents the organization on the open
// entities. A simulation lists what would be done first.
export const ResponsibilityHandoverAction = ({
  personId,
  organizationId,
}: Readonly<Props>) => {
  const { can } = usePermissions();
  const [isOpen, setIsOpen] = useState(false);
  const [replacementPersonId, setReplacementPersonId] = useState('');
  const { data: replacements } = useHandoverReplacementsQuery(isOpen);
  const { mutate, data, error, isPending, reset } =
    useHandOverResponsibilitiesMutation();

  const open = useCallback(() => {
    reset();
    setReplacementPersonId('');
    setIsOpen(true);
  }, [reset]);

  const close = useCallback(() => {
    setIsOpen(false);
  }, []);

  const chooseReplacement = useCallback(
    (event: Readonly<ChangeEvent<HTMLInputElement>>) => {
      reset();
      setReplacementPersonId(event.target.value);
    },
    [reset],
  );

  const handOver = useCallback(
    (dryRun: boolean) => {
      mutate({
        personId,
        organizationId,
        replacementPersonId: replacementPersonId as Id,
        dryRun,
      });
    },
    [mutate, organizationId, personId, replacementPersonId],
  );

  const simulate = useCallback(() => {
    handOver(true);
  }, [handOver]);
  const launch = useCallback(() => {
    handOver(false);
  }, [handOver]);

  if (
    !can(PermissionEnum.InternalThirdPartiesWrite) &&
    !can(PermissionEnum.UsersManage)
  ) {
    return null;
  }

  const candidates = sortedByThirdPartyName(
    replacements?.findSomePeople.filter(({ _id }) => _id !== personId) ?? [],
  );
  const isSimulated = !!data;

  return (
    <>
      <Button onClick={open} size="small">
        Passation de responsabilité
      </Button>
      <Dialog fullWidth maxWidth="md" onClose={close} open={isOpen}>
        <DialogTitle>Passation de responsabilité</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Le remplaçant reçoit les rôles de l&apos;adhésion et devient
            représentant de l&apos;organisation, avec les mêmes rôles, sur les
            dossiers, missions et événements métier ouverts. La personne
            actuelle reste en place.
          </DialogContentText>
          <TextField
            fullWidth
            label="Remplaçant"
            onChange={chooseReplacement}
            select
            sx={{ mt: 2 }}
            value={replacementPersonId}
          >
            {candidates.map(({ _id, fullName }) => (
              <MenuItem key={_id} value={_id}>
                {fullName}
              </MenuItem>
            ))}
          </TextField>
          {error ? (
            <AlertMessages
              messages={describeErrors(error)}
              severity="error"
              sx={{ mt: 2 }}
            />
          ) : null}
          {data ? (
            <HandoverReportTable report={data.handOverResponsibilities} />
          ) : null}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={close} size="large">
            Fermer
          </Button>
          <Button
            disabled={!replacementPersonId || isPending}
            onClick={simulate}
            size="large"
            variant="outlined"
          >
            Simuler
          </Button>
          <Button
            disabled={!replacementPersonId || !isSimulated || isPending}
            onClick={launch}
            size="large"
            variant="contained"
          >
            Lancer
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
